import type { WeiValue } from "@/domains/core"
import type { Config } from "@wagmi/core"
import type { Address } from "viem"
import { routerAbi } from "@/domains/abis"
import { TokenStore } from "@/domains/tokens"
import { readContract } from "@wagmi/core"
import { formatUnits } from "viem"

import type { ReadTransactionReturnTypeError } from "../types"
import { BIGINT_ZERO } from "../constants"

type WithdrawLiquidityQuoteProps = {
  account: Address
  chainId: number
  config: Config
  routerAddress: Address
  token0: TokenStore
  token1: TokenStore
  stable: boolean
  amount: bigint
}

export type WithdrawQuoteTransactionReturnTypeSuccess = {
  success: true
  token0: WeiValue & { value: string }
  token1: WeiValue & { value: string }
}

export type WithdrawQuoteTransactionReturnType =
  | WithdrawQuoteTransactionReturnTypeSuccess
  | ReadTransactionReturnTypeError

export const withdrawLiquidityQuote = async ({
  account,
  chainId,
  config,
  routerAddress,
  token0,
  token1,
  stable,
  amount,
}: WithdrawLiquidityQuoteProps): Promise<WithdrawQuoteTransactionReturnType> => {
  try {
    const result = await readContract(config, {
      account,
      abi: routerAbi,
      address: routerAddress,
      functionName: "quoteRemoveLiquidity",
      args: [token0.address.toViemAddress(), token1.address.toViemAddress(), stable, amount],
      chainId,
    })

    if (Array.isArray(result) && result.length === 2) {
      const weiToken0 = result[0]
      const valueToken0 = formatUnits(weiToken0, token0.decimals)
      const weiToken1 = result[1]
      const valueToken1 = formatUnits(weiToken1, token1.decimals)
      return {
        token0: {
          wei: weiToken0,
          value: valueToken0,
          decimals: token0.decimals,
        },
        token1: {
          wei: weiToken1,
          value: valueToken1,
          decimals: token1.decimals,
        },
        success: true,
      }
    }
    return {
      token0: { wei: BIGINT_ZERO, value: "0", decimals: token0.decimals },
      token1: { wei: BIGINT_ZERO, value: "0", decimals: token1.decimals },
      success: true,
    }
  } catch (error: any) {
    console.error("withdrawLiquidityQuote", error)
    return { success: false, error: error.message, code: error.code }
  }
}
